import { useEffect, useMemo } from 'react';
import type { DrawerTab } from '../state/store';
import { useStore } from '../state/store';
import { statusColors } from '../lib/derive';
import { formatCpu, formatMemory, formatUptime } from '../lib/format';
import { splinePath } from '../lib/chart';
import { Dot, Heading, Placeholder, tabularNums } from './ui';
import { LogRow } from './views/LogRow';

const TABS: { id: DrawerTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'logs', label: 'Logs' },
  { id: 'env', label: 'Environment' },
];

/** The bridge masks secrets with this prefix; see `maskValue` in shared/redact. */
const MASK = '••••••••';

export function ProcessDrawer() {
  const selectedId = useStore((s) => s.selectedId);
  const select = useStore((s) => s.select);
  const tab = useStore((s) => s.drawerTab);
  const setTab = useStore((s) => s.setDrawerTab);
  const detail = useStore((s) => s.detail);
  const detailLoading = useStore((s) => s.detailLoading);
  const detailError = useStore((s) => s.detailError);
  const processes = useStore((s) => s.processes);
  const processCpu = useStore((s) => s.processCpu);
  const cpuCeiling = useStore((s) => s.cpuCeiling);
  const now = useStore((s) => s.now);

  useEffect(() => {
    if (selectedId === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') select(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, select]);

  if (selectedId === null) return null;

  // The live process list is the fresher of the two: it updates every status
  // frame, while `detail` is fetched once when the drawer opens.
  const live = processes.find((p) => p.id === selectedId);
  const colors = statusColors(live?.status ?? detail?.status ?? 'stopped');

  return (
    <div
      role="dialog"
      aria-label={`${live?.name ?? 'Process'} detail`}
      className="scroll-y"
      style={{
        position: 'fixed',
        top: 18,
        right: 18,
        zIndex: 20,
        width: 'min(520px, 92vw)',
        background: 'var(--panel)',
        border: '1px solid var(--line)',
        borderRadius: 18,
        boxShadow: 'var(--drawer)',
        height: 'calc(100vh - 36px)',
        animation: 'drawerIn 200ms ease-out',
      }}
    >
      <div
        style={{
          padding: '22px 24px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 13,
        }}
      >
        <div style={{ marginTop: 9 }}>
          <Dot color={colors.dot} ring={colors.ring} size={9} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              overflowWrap: 'anywhere',
            }}
          >
            {live?.name ?? detail?.name ?? `#${selectedId}`}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--muted)',
              marginTop: 5,
              ...tabularNums,
            }}
          >
            {live
              ? `${live.status} · pid ${live.pid ?? '—'} · up ${formatUptime(
                  live.status === 'online' ? live.uptimeSince : null,
                  now,
                )}`
              : 'loading…'}
          </div>
        </div>
        <button
          type="button"
          className="hoverable"
          onClick={() => select(null)}
          aria-label="Close"
          style={{
            width: 32,
            height: 32,
            flex: 'none',
            borderRadius: 999,
            border: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted)',
            fontSize: 15,
          }}
        >
          ✕
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '0 20px 14px',
          borderBottom: '1px solid var(--line)',
        }}
      >
        {TABS.map((t) => {
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                padding: '7px 14px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                background: on ? 'var(--page)' : 'transparent',
                color: on ? 'var(--ink)' : 'var(--muted)',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {detailError ? (
        <Placeholder title="Could not load the process" detail={detailError} />
      ) : detailLoading && !detail ? (
        <Placeholder title="Loading…" />
      ) : tab === 'overview' ? (
        <DrawerOverview
          cpuHistory={processCpu[selectedId] ?? []}
          cpuCeiling={cpuCeiling}
        />
      ) : tab === 'logs' ? (
        <DrawerLogs />
      ) : (
        <DrawerEnv />
      )}
    </div>
  );
}

function DrawerOverview({
  cpuHistory,
  cpuCeiling,
}: {
  cpuHistory: readonly number[];
  cpuCeiling: number;
}) {
  const detail = useStore((s) => s.detail);
  const detailSeries = useStore((s) => s.detailSeries);
  const selectedId = useStore((s) => s.selectedId);
  const processes = useStore((s) => s.processes);
  const live = processes.find((p) => p.id === selectedId);

  // The client-side ring is fresher; the bridge's own series is the fallback
  // for the moment between opening the drawer and the next status frame.
  const values =
    cpuHistory.length > 1 ? cpuHistory.slice(-60) : detailSeries.map((s) => s.cpu);
  const current = live ?? detail;

  const tiles: { label: string; value: string; color?: string }[] = [
    { label: 'CPU', value: current ? formatCpu(current.cpu) : '—' },
    { label: 'Memory', value: current ? formatMemory(current.memory) : '—' },
    {
      label: 'Restarts',
      value: current ? String(current.restarts) : '—',
      color:
        current && (current.unstableRestarts > 0 || current.restarts > 9)
          ? 'var(--neg)'
          : undefined,
    },
    { label: 'Instances', value: current ? `×${current.instances}` : '—' },
    {
      label: 'Exec mode',
      value: current ? current.execMode.replace('_mode', '') : '—',
    },
    { label: 'Node', value: detail?.nodeVersion ?? '—' },
  ];

  return (
    <div
      style={{
        padding: '20px 24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
          gap: 10,
        }}
      >
        {tiles.map((tile) => (
          <div
            key={tile.label}
            style={{
              background: 'var(--page)',
              borderRadius: 13,
              padding: '14px 15px',
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--faint)' }}>{tile.label}</div>
            <div
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 800,
                fontSize: 21,
                marginTop: 5,
                letterSpacing: '-0.02em',
                overflowWrap: 'anywhere',
                ...(tile.color ? { color: tile.color } : {}),
                ...tabularNums,
              }}
            >
              {tile.value}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <Heading size={15}>
            CPU · last {Math.min(60, Math.max(values.length, 0))}s
          </Heading>
          <div
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: 19,
              letterSpacing: '-0.02em',
              ...tabularNums,
            }}
          >
            {current ? formatCpu(current.cpu) : '—'}
          </div>
        </div>
        <svg
          width="100%"
          height="110"
          viewBox="0 0 460 110"
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          <defs>
            <linearGradient id="drawerFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--pos)" stopOpacity="0.20" />
              <stop offset="100%" stopColor="var(--pos)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[1, 55].map((y) => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="460"
              y2={y}
              stroke="var(--line)"
              strokeWidth="1"
              strokeDasharray="3 5"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <line
            x1="0"
            y1="109"
            x2="460"
            y2="109"
            stroke="var(--line)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={splinePath(values, 460, 110, cpuCeiling, true)}
            fill="url(#drawerFill)"
          />
          <path
            d={splinePath(values, 460, 110, cpuCeiling)}
            fill="none"
            stroke="var(--pos)"
            strokeWidth="2.2"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>

      <div>
        <Heading size={15} style={{ marginBottom: 8 }}>
          Versioning
        </Heading>
        {detail?.versioning ? (
          <>
            <Row label="repository" value={detail.versioning.url ?? '—'} />
            <Row label="branch" value={detail.versioning.branch ?? '—'} />
            <Row
              label="revision"
              value={detail.versioning.revision?.slice(0, 7) ?? '—'}
            />
            {detail.versioning.unstaged === true ? (
              <Row label="working tree" value="has unstaged changes" />
            ) : null}
            {detail.versioning.comment ? (
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--muted)',
                  marginTop: 12,
                  lineHeight: 1.55,
                }}
              >
                “{detail.versioning.comment.split('\n')[0]}”
              </div>
            ) : null}
          </>
        ) : (
          <div
            style={{
              fontSize: 13,
              color: 'var(--muted)',
              lineHeight: 1.55,
              textWrap: 'pretty',
            }}
          >
            pm2 has no git metadata for this process — it was started from a path outside
            a repository, or the repository was not readable when it launched.
          </div>
        )}
      </div>

      <div>
        <Heading size={15} style={{ marginBottom: 8 }}>
          Definition
        </Heading>
        <Row label="script" value={detail?.script ?? live?.script ?? '—'} />
        <Row label="cwd" value={detail?.cwd ?? live?.cwd ?? '—'} />
        <Row label="interpreter" value={detail?.interpreter ?? '—'} />
        <Row label="namespace" value={current?.namespace ?? '—'} />
        <Row
          label="autorestart"
          value={current ? (current.autorestart ? 'on' : 'off') : '—'}
        />
        <Row
          label="max memory restart"
          value={
            current?.maxMemoryRestart ? formatMemory(current.maxMemoryRestart) : 'unset'
          }
        />
        <Row label="watch" value={detail ? (detail.watch ? 'on' : 'off') : '—'} />
        {detail?.exitCode !== null && detail?.exitCode !== undefined ? (
          <Row label="last exit code" value={String(detail.exitCode)} />
        ) : null}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '11px 0',
        borderTop: '1px solid var(--line-soft)',
        fontSize: 13,
      }}
    >
      <div style={{ color: 'var(--muted)', flex: 'none' }}>{label}</div>
      <div
        style={{
          fontWeight: 600,
          textAlign: 'right',
          overflowWrap: 'anywhere',
          ...tabularNums,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function DrawerLogs() {
  const detailLogs = useStore((s) => s.detailLogs);
  const logs = useStore((s) => s.logs);
  const detail = useStore((s) => s.detail);
  const selectedId = useStore((s) => s.selectedId);

  const liveLines = useMemo(
    () =>
      logs
        .filter((l) => l.processId === selectedId)
        .slice(-40)
        .reverse(),
    [logs, selectedId],
  );
  const tail = useMemo(() => [...detailLogs].slice(-60).reverse(), [detailLogs]);
  const lines = tail.length > 0 ? tail : liveLines;

  return (
    <div style={{ padding: '18px 24px 28px' }}>
      <div
        style={{
          fontSize: 12.5,
          color: 'var(--faint)',
          paddingBottom: 10,
          borderBottom: '1px solid var(--line)',
        }}
      >
        {tail.length > 0
          ? `Tail · file source · stdout · ${detail?.outLogPath ?? ''}`
          : 'Live buffer · this process only'}
      </div>
      {lines.length === 0 ? (
        <Placeholder
          title="No output"
          detail="Neither the log file nor the live buffer has anything for this process yet."
        />
      ) : (
        lines.map((line) => (
          <LogRow key={line.key} line={line} showProcess={false} variant="drawer" />
        ))
      )}
    </div>
  );
}

function DrawerEnv() {
  const detail = useStore((s) => s.detail);
  const entries = Object.entries(detail?.env ?? {});

  return (
    <div style={{ padding: '18px 24px 28px' }}>
      <div
        style={{
          fontSize: 13,
          color: 'var(--muted)',
          marginBottom: 12,
          lineHeight: 1.55,
          textWrap: 'pretty',
        }}
      >
        Secrets are masked by the bridge and never leave the server. Only an explicit
        allowlist of operational keys is revealed.
      </div>

      {entries.length === 0 ? (
        <Placeholder
          title="No environment"
          detail="pm2 reports no environment for this process."
        />
      ) : (
        entries.map(([key, value]) => {
          const masked = value.startsWith(MASK);
          return (
            <div
              key={key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                padding: '11px 0',
                borderTop: '1px solid var(--line-soft)',
                fontSize: 13,
              }}
            >
              <div style={{ color: 'var(--muted)', overflowWrap: 'anywhere' }}>{key}</div>
              <div
                style={{
                  textAlign: 'right',
                  fontWeight: 600,
                  color: masked ? 'var(--faint)' : 'var(--ink)',
                  overflowWrap: 'anywhere',
                }}
              >
                {value}
              </div>
            </div>
          );
        })
      )}

      <div
        style={{
          marginTop: 16,
          padding: '14px 16px',
          borderRadius: 13,
          background: 'var(--page)',
          fontSize: 12.5,
          color: 'var(--muted)',
          lineHeight: 1.55,
          textWrap: 'pretty',
        }}
      >
        Lifecycle actions are disabled in this console — restart, stop and delete stay in
        the phone app, where they are confirmed and audited.
      </div>
    </div>
  );
}
